import { useState } from 'react'
import { Button } from '@/app/common/components/button/Button'
import Checkbox from '@/app/common/components/Checkbox'
import SearchInput from '@/app/common/components/SearchInput'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { useProviderAffiliations } from '@/queries/providers/affiliations/hooks'
import { useSyncPurseAffiliations } from '@/queries/purse_configs/useSyncPurseAffiliations'
import { ProgramPurseAffiliation } from '@/queries/program_purses/schemas'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import { useQueryClient } from '@tanstack/react-query'
import { PartialPurseData } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  partialPurseData: PartialPurseData
  currentAffiliations: ProgramPurseAffiliation[]
}

export default function ManageAffiliationsDialog({
  open,
  onOpenChange,
  partialPurseData,
  currentAffiliations,
}: Props) {
  const syncMutation = useSyncPurseAffiliations()
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && syncMutation.status === 'pending') {
      return
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {open && (
        <ManageAffiliationsDialogContent
          onOpenChange={onOpenChange}
          partialPurseData={partialPurseData}
          currentAffiliations={currentAffiliations}
          syncMutation={syncMutation}
        />
      )}
    </Dialog>
  )
}

type ContentProps = Omit<Props, 'open'> & {
  syncMutation: ReturnType<typeof useSyncPurseAffiliations>
}

function ManageAffiliationsDialogContent({
  onOpenChange,
  partialPurseData,
  currentAffiliations,
  syncMutation,
}: ContentProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { query: affiliationsQuery } = useProviderAffiliations()

  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>(() =>
    currentAffiliations.map((a) => a.id)
  )

  const toggleAffiliation = (affiliationId: number) => {
    setSelectedIds((current) =>
      current.includes(affiliationId)
        ? current.filter((id) => id !== affiliationId)
        : [...current, affiliationId]
    )
  }

  const handleSave = () => {
    syncMutation.mutate(
      {
        params: {
          program_id: partialPurseData.programId,
          purse_id: partialPurseData.id,
        },
        payload: {
          affiliation_ids: selectedIds,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_AFFILIATIONS(
              partialPurseData.id,
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          toast({
            title: 'Success',
            description: 'Purse affiliations updated successfully',
          })
          onOpenChange(false)
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        },
      }
    )
  }

  const searchQuery = search.trim().toLowerCase()
  const filteredAffiliations =
    affiliationsQuery.data?.filter((affiliation) => {
      if (searchQuery.length === 0) return true
      return (
        affiliation.name.toLowerCase().includes(searchQuery) ||
        String(affiliation.id).includes(searchQuery)
      )
    }) ?? []

  return (
    <DialogContent
      className="max-w-2xl"
      onInteractOutside={(event) => {
        if (syncMutation.status === 'pending') {
          event.preventDefault()
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>Manage Affiliations</DialogTitle>
        <DialogDescription className="text-left">
          Select which affiliations can be used with this purse.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <SearchInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search affiliations"
          disabled={affiliationsQuery.status === 'pending'}
        />

        <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
          {affiliationsQuery.status === 'pending' && (
            <p className="p-4 text-sm text-gray-600">Loading affiliations...</p>
          )}

          {affiliationsQuery.status === 'error' && (
            <p className="p-4 text-sm text-red-600">
              {affiliationsQuery.error.message}
            </p>
          )}

          {affiliationsQuery.status === 'success' &&
            filteredAffiliations.length === 0 && (
              <p className="p-4 text-sm text-gray-600">
                No affiliations found.
              </p>
            )}

          {affiliationsQuery.status === 'success' &&
            filteredAffiliations.map((affiliation) => {
              const checked = selectedIds.includes(affiliation.id)

              return (
                <label
                  key={affiliation.id}
                  htmlFor={`purse-affiliation-${affiliation.id}`}
                  className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <Checkbox
                        id={`purse-affiliation-${affiliation.id}`}
                        name={`purse-affiliation-${affiliation.id}`}
                        checked={checked}
                        onChange={() => toggleAffiliation(affiliation.id)}
                        disabled={syncMutation.status === 'pending'}
                      />
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {affiliation.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {affiliation.status}
                      </p>
                    </div>
                  </div>
                </label>
              )
            })}
        </div>
      </div>
      <DialogFooter className="gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={syncMutation.status === 'pending'}
        >
          Cancel
        </Button>
        <div>
          <SubmitButton
            buttonTxt="Save Affiliations"
            submittingText="Saving..."
            status={syncMutation.status}
            onClick={handleSave}
          />
        </div>
      </DialogFooter>
    </DialogContent>
  )
}
